import { useCallback, useEffect, useRef, useState } from "react";
import { readPageData } from "../utils/pageStorage";
import { renderPageToCanvas } from "../utils/renderPage";
import { findPageSizePreset, pageSizeToPx } from "../constants/pageDimensions";

// Thumbnails are drawn at this width in CSS px, doubled for sharp screens
export const THUMBNAIL_WIDTH = 92;
const PIXEL_RATIO = 2;
const REFRESH_DELAY_MS = 400;

/** Page size in page px for a saved page. */
export const pageSizeFor = (data) => pageSizeToPx(findPageSizePreset(data?.pageSizeKey || "letter"));

const renderThumbnail = async (pageId) => {
  const data = readPageData(pageId);
  if (!data) return null;
  const { width, height } = pageSizeFor(data);
  const canvas = await renderPageToCanvas(data.cells, {
    pageWidthPx: width,
    pageHeightPx: height,
    comicStyleKey: data.comicStyleKey,
    storyboard: data.storyboard || null,
    scale: (THUMBNAIL_WIDTH * PIXEL_RATIO) / width,
  });
  return { url: canvas.toDataURL("image/png"), aspect: height / width };
};

/**
 * Live page thumbnails, drawn by the same renderer as the exports. Every
 * page renders once on load; `refresh(pageId)` redraws one after it saves.
 * Thumbnails live in memory only.
 */
export const usePageThumbnails = (pages) => {
  const [thumbnails, setThumbnails] = useState({});
  const timers = useRef({});
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    const pendingTimers = timers.current;
    return () => {
      mounted.current = false;
      Object.values(pendingTimers).forEach((t) => window.clearTimeout(t));
    };
  }, []);

  const draw = useCallback(async (pageId) => {
    try {
      const thumb = await renderThumbnail(pageId);
      if (thumb && mounted.current) setThumbnails((prev) => ({ ...prev, [pageId]: thumb }));
    } catch (e) {
      // A thumbnail failing to draw shouldn't disturb editing
    }
  }, []);

  // Draw any page we don't have yet (on load, after add/duplicate)
  useEffect(() => {
    pages.forEach((page) => {
      if (!thumbnails[page.id]) draw(page.id);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pages, draw]);

  const refresh = useCallback((pageId) => {
    window.clearTimeout(timers.current[pageId]);
    timers.current[pageId] = window.setTimeout(() => draw(pageId), REFRESH_DELAY_MS);
  }, [draw]);

  return { thumbnails, refresh };
};
